import { useCallback, useRef, type RefObject } from 'react';
import { useGetScreensInfiniteQuery } from './hooks/useGetScreensInfiniteQuery';
import { useGetScreensMetaQuery } from './hooks/useGetScreensMetaQuery';
import type { AtomicRendererRefCurrent } from './types';
import { Flex, Spinner, useScreenSize } from '@banhala/ui-kit';

import { useScrollToFilterList } from './hooks/useScrollToFilterList';
import { useLogViewEvent } from './hooks/useLogViewEvent';
import type { SetState } from 'shared/src/types';
import type { ScreensMetaResponse } from 'shared/src/api';
import { skeletonColumnTypeFromApiValue } from 'shared/src/api';
import { useSetTitle } from './hooks/useSetTitle';
import { useSetDropdownNavigator } from './hooks/useSetDropdownNavigator';
import { AtomicRenderer, useFilterListAtom } from 'shared/src/component';
import { useScrollRestoration } from 'shared/src/hooks/useScrollRestoration';
import { retainedState } from 'shared/src/hooks/retainedState';
import { scheduleOnRN } from 'react-native-worklets';
import { useGetCommonParams } from '../hooks';

// visit(retainedState) 단위 스크롤 위치 슬롯. 가려진 화면 unmount→remount 를
// 가로질러 Screens 리스트의 스크롤 위치를 보존한다.
const scrollOffsetSlot = retainedState(0);

type ScreensResultData = NonNullable<
  ReturnType<typeof useGetScreensInfiniteQuery>['data']
>;

type ScreensResultProps = {
  setTitle?: SetState<string | undefined>;
  setDropdownNavigator?: SetState<
    ScreensMetaResponse['screen']['dropdown_navigator'] | null
  >;
  onScrollY?: (scrollY: number) => void;
  onBeginDrag?: (scrollY: number) => void;
  onEndDrag?: (scrollY: number) => void;
  listRef?: RefObject<AtomicRendererRefCurrent>;
  hideScreenToTop?: boolean;
  /**
   * VIEW 이벤트 발화 게이트. false면 발화하지 않고 true로 전이될 때마다 재발화.
   * brownfield(FragmentScreens)에서 `useIsFocusedTab` 값을 주입해 native tab
   * 재진입 시 재발화시키는 용도.
   */
  viewLogEnabled?: boolean;
  /**
   * visit 단위 스크롤 위치 저장/복원 활성화. Screens.page 처럼 react-navigation
   * 스택으로 마운트되는(가려질 때 unmount 될 수 있는) 화면에서만 켠다. native tab 호스트
   * (FragmentScreens)는 기본 off.
   */
  enableScrollRestoration?: boolean;

  isForeground: boolean;
};

export const ScreensResult = ({
  setTitle,
  setDropdownNavigator,
  onScrollY,
  onBeginDrag,
  onEndDrag,
  hideScreenToTop,
  listRef: externalListRef,
  viewLogEnabled,
  enableScrollRestoration,
  isForeground,
}: ScreensResultProps) => {
  const internalListRef = useRef<AtomicRendererRefCurrent>(null);
  const atomicRendererRef = externalListRef ?? internalListRef;
  const { nextToken } = useGetCommonParams();

  const { data, fetchNextPage, isFetching, isLoading, refetch } =
    useGetScreensInfiniteQuery(nextToken);

  if (isLoading) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <ScreensResultContent
      data={data}
      atomicRendererRef={atomicRendererRef}
      fetchNextPage={fetchNextPage}
      refetch={refetch}
      isFetching={isFetching}
      setTitle={setTitle}
      setDropdownNavigator={setDropdownNavigator}
      onScrollY={onScrollY}
      onBeginDrag={onBeginDrag}
      onEndDrag={onEndDrag}
      hideScreenToTop={hideScreenToTop}
      viewLogEnabled={viewLogEnabled}
      enableScrollRestoration={enableScrollRestoration}
      isForeground={isForeground}
    />
  );
};

type ScreensResultContentProps = {
  data: ScreensResultData;
  atomicRendererRef: RefObject<AtomicRendererRefCurrent>;
  fetchNextPage: VoidFunction;
  refetch: VoidFunction;
  isFetching: boolean;
  setTitle?: SetState<string | undefined>;
  setDropdownNavigator?: SetState<
    ScreensMetaResponse['screen']['dropdown_navigator'] | null
  >;
  onScrollY?: (scrollY: number) => void;
  onBeginDrag?: (scrollY: number) => void;
  onEndDrag?: (scrollY: number) => void;
  hideScreenToTop?: boolean;
  viewLogEnabled?: boolean;
  enableScrollRestoration?: boolean;
  isForeground: boolean;
};

/**
 * 데이터가 준비된 뒤에만 마운트되는 실제 리스트.
 * listRef / useScrollRestoration 이 여기서 리스트와 함께 마운트되므로, 복원이 호출될 때
 * listRef.current 가 항상 살아있다. (useGetScreensInfiniteQuery 가 staleTime/gcTime 0 이라
 * visit 복귀마다 풀 로딩 → 그 동안 리스트가 미마운트되는 문제 대응)
 */
const ScreensResultContent = ({
  data,
  atomicRendererRef,
  fetchNextPage,
  refetch,
  isFetching,
  setTitle,
  setDropdownNavigator,
  onScrollY,
  onBeginDrag,
  onEndDrag,
  hideScreenToTop,
  viewLogEnabled,
  enableScrollRestoration,
  isForeground,
}: ScreensResultContentProps) => {
  const itemHeightsRef = useRef<{ [key: number]: number }>({});
  const { filterListAtomValue } = useFilterListAtom();
  const { data: metaData } = useGetScreensMetaQuery();
  // iOS `MetaFeedReactor.fetchScreenAppreanceData` / Android `ComponentMetaViewModel`
  // 와 1:1 대칭. 서버 MetaScreenSerializer default가 "2col"이라 모든 화면에서 값이 내려옴.
  const skeletonType = skeletonColumnTypeFromApiValue(
    metaData?.screen.skeleton_type,
  );
  useLogViewEvent({
    logging: data.logging,
    screenName: data.screenName,
    enabled: viewLogEnabled,
  });
  const {
    filterListIndex,
    scrollToFilterList,
    onItemHeightChange,
    isPendingScroll,
  } = useScrollToFilterList({
    atomicRendererRef,
    components: data.components,
    isFetching,
    itemHeightsRef,
  });
  useSetTitle({ setTitle, title: data.title });
  useSetDropdownNavigator({
    setDropdownNavigator,
    dropdownNavigator: metaData?.screen.dropdown_navigator,
  });

  // 스크롤 위치 저장/복원 (visit 단위). 데이터 준비 후 listRef 와 함께 마운트되므로 복원 시
  // ref 가 살아있다. enableScrollRestoration 이 false 면 save 를 호출하지 않아 사실상 inert.
  const { height: screenHeight } = useScreenSize();
  const scrollToOffset = useCallback(
    (offset: number, animated: boolean) => {
      atomicRendererRef.current?.scrollToOffset({ offset, animated });
    },
    [atomicRendererRef],
  );
  const scrollRestoration = useScrollRestoration({
    token: scrollOffsetSlot,
    scrollTo: scrollToOffset,
    viewportHeight: screenHeight,
  });

  // 스크롤 종료(onEndDrag / onMomentumEnd) 시점에 현재 offset 저장.
  const handleScrollEndWorklet = useCallback(
    (y: number) => {
      'worklet';
      scheduleOnRN(scrollRestoration.save, y);
    },
    [scrollRestoration.save],
  );

  return (
    <Flex flex={1}>
      {isFetching && (
        <Flex.Center
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          backgroundColor="bg_primary"
          backgroundColorOpacity={isPendingScroll ? 0.5 : 0}
          zIndex={1000}
          pointerEvents={isPendingScroll ? undefined : 'none'}
        >
          <Spinner size={20} />
        </Flex.Center>
      )}
      <AtomicRenderer
        isForeground={isForeground}
        refetch={refetch}
        data={data}
        fetchNextPage={fetchNextPage}
        listRef={atomicRendererRef}
        filterListIndex={filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        isPendingScroll={isPendingScroll}
        itemHeightsRef={itemHeightsRef}
        onItemHeightChange={onItemHeightChange}
        onScrollY={onScrollY}
        onBeginDrag={onBeginDrag}
        onEndDrag={onEndDrag}
        filterParams={filterListAtomValue}
        skeletonType={skeletonType}
        hideScrollToTop={hideScreenToTop}
        onScrollEndWorklet={
          enableScrollRestoration ? handleScrollEndWorklet : undefined
        }
        onContentSizeChange={
          enableScrollRestoration
            ? scrollRestoration.onContentSizeChange
            : undefined
        }
        listStyle={
          enableScrollRestoration && scrollRestoration.restoring
            ? { opacity: 0 }
            : undefined
        }
      />
    </Flex>
  );
};
